import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import RjsfForm, { IChangeEvent } from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { FlowFormData, FlowMappingsConfig } from './types';

type RjsfFormInstance = InstanceType<typeof RjsfForm>;

/** Merges API uiSchema with app-only presentation for WhatsApp flow mapping fields. */
export const getMappingsUiSchema = (mappingsConfig: FlowMappingsConfig): Record<string, unknown> => {
  if (!mappingsConfig) {
    return {};
  }
  const mappingsUiSchema = (mappingsConfig.uiSchema ?? {}) as Record<string, unknown>;
  const userDataAddressUiSchema = (mappingsUiSchema['userDataAddress'] ?? {}) as Record<string, unknown>;
  const userDataIdentifierUiSchema = (mappingsUiSchema['userDataIdentifier'] ?? {}) as Record<string, unknown>;

  return {
    ...mappingsUiSchema,
    ['userDataAddress']: {
      'ui:classNames': 'hide-control-label-checkbox',
      ...userDataAddressUiSchema,
    },
    ['userDataIdentifier']: {
      'ui:classNames': 'hide-control-label-checkbox',
      ...userDataIdentifierUiSchema,
    },
  };
};

export type FlowMappingsFormProps = {
  config: NonNullable<FlowMappingsConfig>;
  formData: FlowFormData;
  onFormDataChange: (data: FlowFormData) => void;
  onValidationChange?: (isValid: boolean) => void;
};

export type FlowMappingsFormHandle = {
  revealValidationErrors: () => void;
};

const reportValidity = (data: FlowFormData, schema: NonNullable<FlowMappingsConfig>['schema']) => {
  const { errors } = validator.validateFormData(data, schema);
  return errors.length === 0;
};

/**
 * Server-driven RJSF surface for connector flow **mappings** (data mapping step).
 * Same RJSF baseline props as MultiStepFlowForm in MultiStepFlowFormv2.tsx (params step forms).
 */
export const FlowMappingsForm = forwardRef<FlowMappingsFormHandle, FlowMappingsFormProps>(function FlowMappingsForm(
  { config, formData, onFormDataChange, onValidationChange },
  ref
) {
  const formRef = useRef<RjsfFormInstance | null>(null);
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    setShowValidationErrors(false);
  }, [config.schema, config.uiSchema]);

  useLayoutEffect(() => {
    onValidationChangeRef.current?.(reportValidity(formData, config.schema));
  }, [config.schema, config.uiSchema, formData]);

  useEffect(() => {
    if (!showValidationErrors || !formRef.current) {
      return;
    }
    const isValid = formRef.current.validateForm();
    onValidationChangeRef.current?.(isValid);
  }, [showValidationErrors]);

  const revealValidationErrors = useCallback(() => {
    setShowValidationErrors(true);
  }, []);

  useImperativeHandle(ref, () => ({ revealValidationErrors }), [revealValidationErrors]);

  return (
    <div className='whatsapp-flow-rjsf rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
      <RjsfForm
        ref={formRef}
        className='rjsf'
        tagName='div'
        noHtml5Validate
        liveValidate={showValidationErrors}
        schema={config.schema}
        uiSchema={getMappingsUiSchema(config)}
        formData={formData}
        onChange={(event: IChangeEvent) => {
          const data = (event.formData as FlowFormData) ?? {};
          onFormDataChange(data);
          const isValid = showValidationErrors ? event.errors.length === 0 : reportValidity(data, config.schema);
          onValidationChangeRef.current?.(isValid);
        }}
        validator={validator}
      >
        <Fragment />
      </RjsfForm>
    </div>
  );
});
